// format.ts - indent-only YAML tidy: the one place that decides what a line's
// leading whitespace should be, for the format command, format-on-save, and
// the paste filter.
//
// This is deliberately not a YAML formatter in the Prettier sense. It never
// parses-and-reserializes, never touches quoting, key order, comments, or
// blank-line runs - it moves leading whitespace and nothing else. That is what
// lets format-on-save default to on: on a correctly-indented file it is a
// no-op, so it cannot manufacture a diff in a git-backed source of truth
// (`apps/cloud/apps/connections/source_yaml.py` rejected reserialization
// server-side for the same reason).
//
// Two operations live here, and they are different problems:
//
//   `tidyYaml` repairs a whole document - each structure line snaps to the
//   nearest indent level that is actually open at that point.
//
//   The paste filter re-bases a pasted block: the block keeps its own internal
//   shape and moves as a unit to the depth the context calls for. Snapping a
//   pasted block line-by-line would destroy the structure it arrived with.

import { getIndentation, getIndentUnit, indentString } from "@codemirror/language";
import type { SyntaxNode } from "@lezer/common";
import { parser as yamlParser } from "@lezer/yaml";
import { EditorState, type ChangeSpec, type Extension } from "@codemirror/state";
import { keymap, type Command, type EditorView } from "@codemirror/view";
import { opensIndent } from "./indent.js";

/**
 * Node types that hold other lines' structure rather than being content
 * themselves. A line starting inside one of these is a line of its own; a line
 * starting inside anything else (a wrapped plain scalar, a multi-line flow
 * collection, a block-scalar body) is a *continuation* and has no indent of its
 * own to decide. Listing the containers rather than the content nodes makes the
 * unknown case safe: a node type this list has never heard of is treated as
 * content and left alone.
 */
const CONTAINER_NODES = new Set(["Stream", "Document", "BlockMapping", "BlockSequence", "Pair", "Item", "Key"]);

const BLOCK_LITERAL_CONTENT = "BlockLiteralContent";
const COMMENT = "Comment";

/** A `- ` item opener, capturing the dash's indent and the spaces after it. */
const LIST_ITEM_RE = /^([ \t]*)-([ \t]+)\S/;

/**
 * How a single line is treated. `structure` lines get a computed indent;
 * `continuation` lines shift by whatever their owning structure line shifted,
 * so a SQL body's internal shape survives its header moving.
 */
interface LinePlan {
  kind: "structure" | "continuation";
  /** Continuations only: whether the line sits inside a block-scalar body. */
  inBlockScalar: boolean;
  /**
   * Offset within the line where a real comment begins, or null. Taken from
   * the tree, so a `#` inside a quoted value ("Rate: #1") is not one.
   */
  commentStart: number | null;
}

/** Visual column of the leading whitespace, counting a tab as one indent unit. */
function visualIndent(line: string, unitWidth: number): number {
  let column = 0;
  for (const char of line) {
    if (char === " ") column += 1;
    else if (char === "\t") column += unitWidth;
    else break;
  }
  return column;
}

function contentStart(line: string): number {
  const match = /^[ \t]*/.exec(line);
  return match ? match[0].length : 0;
}

/**
 * Classifies every line of `source`. Line starts are compared against the
 * parsed tree: a line whose start offset falls strictly inside a non-container
 * node belongs to a construct that began earlier.
 */
function planLines(source: string, lineStarts: number[]): LinePlan[] {
  // Full parse, never `syntaxTree(state)`: CM6's incremental parse is
  // budget-truncated to a prefix of the document, which would silently leave
  // block scalars past that point looking like ordinary structure - and
  // reindenting a SQL body rewrites the query.
  const tree = yamlParser.parse(source);

  // Where a real comment starts on each line. Sourced from the tree because
  // only the tree can tell `revenue: # note` (a comment that hides the colon
  // opening a level) from `title: "Rate: #1"` (a `#` inside a value).
  const commentStarts = new Map<number, number>();
  const comments = tree.cursor();
  do {
    if (comments.name !== COMMENT) continue;
    const index = lineIndexAt(lineStarts, comments.from);
    if (!commentStarts.has(index)) commentStarts.set(index, comments.from - lineStarts[index]);
  } while (comments.next());

  return lineStarts.map((start, index) => {
    let inBlockScalar = false;
    let continuation = false;
    // Only this line's own ancestors can cover its start, so resolving once and
    // walking up costs the tree's depth per line rather than a pass over every
    // line per node.
    for (let node: SyntaxNode | null = tree.resolveInner(start, 1); node; node = node.parent) {
      // Error nodes are skipped, not treated as content: lezer's recovery spans
      // whole regions of a broken document, and a document mid-repair is exactly
      // when the formatter has work to do. Trusting those spans would classify
      // real structure lines as untouchable continuations.
      if (node.type.isError || CONTAINER_NODES.has(node.name)) continue;
      if (node.from >= start) continue;
      continuation = true;
      if (node.name === BLOCK_LITERAL_CONTENT) inBlockScalar = true;
    }
    const commentStart = commentStarts.get(index) ?? null;
    return continuation
      ? { kind: "continuation", inBlockScalar, commentStart }
      : { kind: "structure", inBlockScalar: false, commentStart };
  });
}

/** Index of the line containing `offset`, by binary search over line starts. */
function lineIndexAt(lineStarts: number[], offset: number): number {
  let low = 0;
  let high = lineStarts.length - 1;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (lineStarts[mid] <= offset) low = mid;
    else high = mid - 1;
  }
  return low;
}

/**
 * The indent level a structure line should sit at: the open level nearest the
 * one its author typed, preferring the deeper level on a tie. Snapping to an
 * *open* level is the whole difference between this and `dbtChartsIndentService`
 * - the service maintains the previous line's indent and so cannot repair a
 * line that matches no parent at all, which is exactly the crooked-paste and
 * one-space-drift case.
 */
function nearestLevel(levels: number[], authored: number): number {
  let best = levels[0];
  for (const level of levels) {
    const closer = Math.abs(level - authored) < Math.abs(best - authored);
    const tieButDeeper = Math.abs(level - authored) === Math.abs(best - authored) && level > best;
    if (closer || tieButDeeper) best = level;
  }
  return best;
}

/**
 * Repairs the indentation of `source`, trims trailing whitespace off structure
 * lines, and ends the document in at least one newline - a trailing blank-line
 * run the author left is preserved, since collapsing it is reflow rather than
 * indentation. Pure: the indent unit is the caller's, never a literal here, so
 * it always agrees with the editor's own `indentUnit` facet.
 */
export function tidyYaml(source: string, unit: string): string {
  const lines = source.split("\n");
  const lineStarts: number[] = [];
  let offset = 0;
  for (const line of lines) {
    lineStarts.push(offset);
    offset += line.length + 1;
  }
  const plans = planLines(source, lineStarts);
  const unitWidth = unit.length;

  const openLevels = [0];
  let previousIndent = 0;
  let previousOpens = false;
  let ownerDelta = 0;
  const output: string[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const plan = plans[index];

    if (plan.kind === "continuation") {
      // A whitespace-only line carries no content in any construct; shifting it
      // would only add bytes to a blank line inside a query.
      output.push(line.trim() === "" ? line : shiftLine(line, ownerDelta, unitWidth));
      continue;
    }
    if (line.trim() === "") {
      output.push("");
      continue;
    }
    // A comment owes YAML no particular indent - it is not structure, and
    // authors deliberately hang explanatory blocks off to the side. So it is
    // passed through untouched and, just as importantly, never becomes an open
    // level that the lines under it could then snap to.
    if (line.trimStart().startsWith("#")) {
      output.push(line.replace(/[ \t]+$/, ""));
      continue;
    }

    const authored = visualIndent(line, unitWidth);
    const candidates = previousOpens ? [...openLevels, previousIndent + unitWidth] : openLevels;
    const target = nearestLevel(candidates, authored);

    while (openLevels.length > 1 && openLevels[openLevels.length - 1] > target) openLevels.pop();
    if (openLevels[openLevels.length - 1] !== target) openLevels.push(target);

    const text = line.slice(contentStart(line)).replace(/[ \t]+$/, "");
    output.push(" ".repeat(target) + text);
    ownerDelta = target - authored;

    // A populated `- ` item opens a level at the column after the dash: the
    // item's own keys live there, not at the dash's indent. That column is
    // also what a trailing `key:` on the same line indents *from* - the level
    // `- cols:` opens is two past `cols`, not two past the dash.
    const listItem = LIST_ITEM_RE.exec(line);
    previousIndent = target;
    if (listItem) {
      previousIndent = target + 1 + listItem[2].length;
      if (openLevels[openLevels.length - 1] !== previousIndent) openLevels.push(previousIndent);
    }
    // Cut the line at its comment first: the pattern anchors at end of line,
    // so `revenue: # sql to a database` would otherwise read as ending in `e`
    // and every child would snap up to this line's own level.
    previousOpens = opensIndent(plan.commentStart === null ? text : line.slice(0, plan.commentStart));
  }

  const tidied = output.join("\n");
  return tidied.endsWith("\n") || tidied === "" ? tidied : `${tidied}\n`;
}

/** Re-indents one line by `delta` columns, never past column zero. */
function shiftLine(line: string, delta: number, unitWidth: number): string {
  if (delta === 0) return line;
  const authored = visualIndent(line, unitWidth);
  return " ".repeat(Math.max(0, authored + delta)) + line.slice(contentStart(line));
}

function unitFor(state: EditorState): string {
  return indentString(state, getIndentUnit(state));
}

/**
 * The tidy expressed as changes to each line's *whitespace* rather than one
 * whole-document replacement. A replacement maps every position inside it to
 * its start, which would drop the caret to the top of the file on every
 * format; touching only the leading and trailing whitespace leaves every
 * position in the text itself mapping exactly where it was.
 *
 * Safe because the tidy only ever moves whitespace: the trimmed body of line N
 * is identical before and after, so a line's change is fully described by its
 * two whitespace ends.
 *
 * The line a cursor sits on keeps its trailing whitespace, and a
 * whitespace-only cursor line is left entirely alone. Format-on-save runs off
 * an idle timer, so it fires during the pause after `title: ` - trimming there
 * would splice the next character onto the colon, and emptying a fresh
 * indented line would drop the caret to column 0. Indentation repair still
 * applies to a cursor's line; only the trailing end is off limits.
 */
export function tidyChanges(state: EditorState): ChangeSpec[] {
  const doc = state.doc;
  const source = doc.toString();
  const tidied = tidyYaml(source, unitFor(state));
  if (tidied === source) return [];

  const cursorLines = new Set(state.selection.ranges.map((range) => doc.lineAt(range.head).number));
  const tidiedLines = tidied.split("\n");
  const changes: ChangeSpec[] = [];
  for (let number = 1; number <= doc.lines; number += 1) {
    const line = doc.line(number);
    if (cursorLines.has(number) && line.text.trim() === "") continue;
    // Line-for-line: the tidy rewrites whitespace and may append a final
    // newline, so it never emits fewer lines than the source has.
    const next = tidiedLines[number - 1];
    if (next === line.text) continue;
    const oldIndent = contentStart(line.text);
    const newIndent = contentStart(next);
    if (line.text.slice(0, oldIndent) !== next.slice(0, newIndent)) {
      changes.push({ from: line.from, to: line.from + oldIndent, insert: next.slice(0, newIndent) });
    }
    if (cursorLines.has(number)) continue;
    const oldBody = line.text.replace(/[ \t]+$/, "");
    const newBody = next.replace(/[ \t]+$/, "");
    if (line.text.length !== oldBody.length || next.length !== newBody.length) {
      changes.push({ from: line.from + oldBody.length, to: line.to, insert: next.slice(newBody.length) });
    }
  }
  if (tidiedLines.length > doc.lines) changes.push({ from: doc.length, insert: "\n" });
  return changes;
}

/**
 * Format the whole document. Returns false without dispatching when the tidy
 * changes nothing, so `Shift-Alt-F` on a clean file is not an undo step.
 */
export const formatYamlDocument: Command = (view: EditorView): boolean => {
  // Cloud mounts the YAML bundle into the read-only merge-review pane, and CM6
  // runs keymap handlers there regardless of `editable` - every upstream
  // mutating command carries this same guard.
  if (view.state.readOnly) return false;
  const changes = tidyChanges(view.state);
  if (!changes.length) return false;
  view.dispatch({ changes, userEvent: "format" });
  return true;
};

/**
 * The re-base for a pasted block: shift every pasted line by the difference
 * between the first line's authored indent and the indent this context calls
 * for. The block keeps its internal shape, which per-line snapping would
 * destroy - a pasted `style:` / `  orientation:` pair has to stay a pair.
 *
 * Skipped when the paste lands mid-line (there is no block to re-base, only
 * text spliced into a value) or inside a block scalar (that is SQL, and its
 * indentation is content).
 */
function pasteReindent(state: EditorState, from: number, to: number): ChangeSpec[] {
  const doc = state.doc;
  const firstLine = doc.lineAt(from);
  if (doc.sliceString(firstLine.from, from).trim() !== "") return [];

  const source = doc.toString();
  const lineStarts: number[] = [];
  for (let number = 1; number <= doc.lines; number += 1) lineStarts.push(doc.line(number).from);
  const plans = planLines(source, lineStarts);
  if (plans[firstLine.number - 1].kind === "continuation") return [];

  const unit = unitFor(state);
  const authored = visualIndent(firstLine.text, unit.length);
  // What a fresh line here should be indented to, straight from
  // `dbtChartsIndentService` - the same answer Enter would have given, so paste
  // and typing never disagree about where this block belongs.
  const desired = getIndentation(state, firstLine.from);
  if (desired === null) return [];
  const delta = desired - authored;
  if (delta === 0) return [];

  const changes: ChangeSpec[] = [];
  // `to` is one past the last inserted character, so a paste ending in a
  // newline reports the *start* of the following line. That line was not
  // pasted; shifting it would silently reparent it, and format-on-save would
  // commit the change.
  const lastLine = doc.lineAt(to > from ? to - 1 : to);
  // Every line in the span shifts, continuations included: the block is moving
  // as a unit, so nothing here is being re-indented on its own. Leaving a
  // wrapped value or a `sql: |` body behind its own header would drift them
  // apart, and a large enough shift would push the header past its own
  // continuation - a parse error, not a cosmetic one.
  for (let number = firstLine.number; number <= lastLine.number; number += 1) {
    const line = doc.line(number);
    if (line.text.trim() === "") continue;
    const indent = visualIndent(line.text, unit.length);
    changes.push({
      from: line.from,
      to: line.from + contentStart(line.text),
      insert: " ".repeat(Math.max(0, indent + delta)),
    });
  }
  return changes;
}

/**
 * The YAML formatting bundle: `Shift-Alt-F` (vscode's format-document binding)
 * and the paste re-base. Mounted with the YAML language, never in
 * `dbtChartsKeymap()` - the chart editor mounts that keymap over SQL, where
 * every rule in this module is wrong.
 *
 * The paste re-base is a `transactionFilter`, not a follow-up dispatch, so the
 * paste and its reindent are a single transaction and a single undo step.
 */
export function dbtChartsFormatExtensions(): Extension[] {
  return [
    keymap.of([{ key: "Shift-Alt-f", run: formatYamlDocument }]),
    EditorState.transactionFilter.of((tr) => {
      if (!tr.docChanged || !tr.isUserEvent("input.paste")) return tr;
      // Each inserted region is re-based on its own: a multi-cursor paste is
      // one transaction with several insertions, and the text *between* them
      // was never pasted.
      const pasted: { from: number; to: number }[] = [];
      tr.changes.iterChanges((_fromA, _toA, fromB, toB) => {
        pasted.push({ from: fromB, to: toB });
      });
      const changes = pasted.flatMap((range) => pasteReindent(tr.state, range.from, range.to));
      return changes.length ? [tr, { changes, sequential: true, userEvent: "input.paste" }] : tr;
    }),
  ];
}
